import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Empty,
  Badge,
  Tooltip,
  Select,
  Modal,
  Form,
  Divider,
  Upload,
  Alert,
  Switch
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  ImportOutlined,
  NumberOutlined,
  CalendarOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SecurityScanOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest, postRequest, deleteRequestNewApi } from "../../../Utils/AppApi";
import * as XLSX from 'xlsx';
import { CSVLink } from "react-csv";
import moment from "moment";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FINGBankManagement = ({ history, onStatsUpdate }) => {
  const [form] = Form.useForm();
  const csvLinkRef = useRef(null);
  
  // Data states
  const [banks, setBanks] = useState([]);
  const [editingBank, setEditingBank] = useState(null);
  const [exportData, setExportData] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("bankName");
  
  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add New FING Bank");
  
  // Import states
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Pagination states
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    pages: 1
  });

  // API endpoints
  const API_ENDPOINTS = {
    LIST: '/admin/banks/fing/getAllFingBanks',
    SAVE: '/admin/banks/fing/saveFingBank',
    UPDATE: '/admin/banks/fing/updateFingBank',
    DELETE: '/admin/banks/fing/removeFingBank',
    EXPORT: '/admin/banks/fing/exportFingBanks',
    IMPORT: '/admin/banks/fing/bulkImportFing'
  };

  /** FETCH ALL BANKS (without pagination) FOR EXPORT */
  const fetchAllBanksForExport = useCallback(async () => {
    try {
      const res = await getRequest(API_ENDPOINTS.EXPORT, history);
      if (res?.success && Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    } catch (error) {
      console.error("Export fetch error:", error);
      toast.error("Failed to fetch complete bank list for export");
      return [];
    }
  }, [history]);

  /** FETCH BANKS with pagination */
  const fetchBanks = useCallback(() => {
    setLoading(true);
    const params = {
      page: pagination.current,
      limit: pagination.pageSize,
      ...(searchTerm && { search: searchTerm })
    };
    
    const queryString = new URLSearchParams(params).toString();
    
    getRequest(`${API_ENDPOINTS.LIST}?${queryString}`, history)
      .then(res => {
        if (res?.success) {
          setBanks(res.data || []);
          if (res.pagination) {
            setPagination({
              ...pagination,
              total: res.pagination.total,
              pages: res.pagination.pages
            });
          }
          // Update stats for parent component
          if (onStatsUpdate) {
            const activeCount = (res.data || []).filter(b => b.activeFlag === 1).length;
            onStatsUpdate({
              total: res.pagination?.total || res.data?.length || 0,
              active: activeCount
            });
          }
        }
      })
      .catch(err => {
        toast.error("Failed to fetch FING banks");
        console.error("Error fetching FING banks:", err);
      })
      .finally(() => setLoading(false));
  }, [history, pagination.current, pagination.pageSize, searchTerm]);

  /** HANDLE TABLE PAGINATION */
  const handleTableChange = (page, totalRows) => {
    setPagination(prev => ({
      ...prev,
      current: page
    }));
  };

  /** HANDLE PER PAGE CHANGE */
  const handlePerRowsChange = (currentRowsPerPage, currentPage) => {
    setPagination(prev => ({
      ...prev,
      pageSize: currentRowsPerPage,
      current: currentPage
    }));
  };

  /** SEARCH BANKS */
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  /** OPEN ADD MODAL */
  const openAddModal = () => {
    setEditingBank(null);
    setModalTitle("Add New FING Bank");
    form.resetFields();
    form.setFieldsValue({ 
      activeFlag: 1,
      face_auth: 'N',
      aadharPay: 'Y',
      cashDeposit: 'Y'
    });
    setModalVisible(true);
  };

  /** OPEN EDIT MODAL */
  const openEditModal = (bank) => {
    setEditingBank(bank);
    setModalTitle("Edit FING Bank");
    form.setFieldsValue({
      bankId: bank.bankId || "",
      bankName: bank.bankName || "",
      iinno: bank.iinno || "",
      face_auth: bank.face_auth || 'N',
      aadharPay: bank.aadharPay || 'Y',
      cashDeposit: bank.cashDeposit || 'Y',
      activeFlag: bank.activeFlag === 1,
      remarks: bank.remarks || "",
      details: bank.details || "",
      timestamp: bank.timestamp || moment().format('YYYY-MM-DD HH:mm:ss')
    });
    setModalVisible(true);
  };

  /** CLOSE MODAL */
  const closeModal = () => {
    setModalVisible(false);
    setEditingBank(null);
    form.resetFields();
  };

  /** SAVE / UPDATE BANK */
  const handleSave = async (values) => {
    if (!values.bankName?.trim()) return toast.error("Bank name is required");
    
    // Validate bankId for new entries
    if (!editingBank && values.bankId) {
      if (isNaN(values.bankId)) {
        return toast.error("Bank ID must be a number");
      }
      if (values.bankId <= 0) {
        return toast.error("Bank ID must be a positive number");
      }
    }

    setActionLoading(true);

    try {
      const url = editingBank ? `${API_ENDPOINTS.UPDATE}/${editingBank._id}` : API_ENDPOINTS.SAVE;
      const method = editingBank ? "PUT" : "POST";
      
      // Format data for FING schema
      const payload = {
        bankName: values.bankName,
        bankId: values.bankId || null,
        iinno: values.iinno || null,
        face_auth: values.face_auth || 'N',
        aadharPay: values.aadharPay || 'Y',
        cashDeposit: values.cashDeposit || 'Y',
        activeFlag: values.activeFlag ? 1 : 0,
        remarks: values.remarks || null,
        details: values.details || null,
        timestamp: values.timestamp || null
      };

      // Remove empty fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const res = await postRequest(url, payload, history, method);
      
      if (res?.success) {
        toast.success(res.message || (editingBank ? "FING bank updated successfully" : "FING bank added successfully"));
        closeModal();
        fetchBanks();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  /** REMOVE BANK */
  const handleRemove = async (id) => {
    setRemovingId(id);
    try {
      const res = await deleteRequestNewApi(`${API_ENDPOINTS.DELETE}/${id}`, history);
      if (res?.success) {
        toast.success("FING bank removed successfully");
        fetchBanks();
      } else {
        toast.error(res?.message);
      }
    } catch {
      toast.error("Remove failed");
    } finally {
      setRemovingId(null);
    }
  };

  /** PREPARE EXPORT DATA (using all banks) */
  const prepareExportData = useCallback(async () => {
    const allBanks = await fetchAllBanksForExport();
    if (!allBanks.length) {
      toast.warning("No data to export");
      return [];
    }
    const data = allBanks.map(bank => ({
      'Bank ID': bank.bankId || '',
      'Bank Name': bank.bankName || '',
      'IIN Number': bank.iinno || '',
      'Face Auth': bank.face_auth === 'Y' ? 'Enabled' : 'Disabled',
      'Aadhar Pay': bank.aadharPay === 'Y' ? 'Enabled' : 'Disabled',
      'Cash Deposit': bank.cashDeposit === 'Y' ? 'Enabled' : 'Disabled',
      'Status': bank.activeFlag === 1 ? 'Active' : 'Inactive',
      'Remarks': bank.remarks || '',
      'Details': bank.details || '',
      'Timestamp': bank.timestamp || '',
      'Created At': moment(bank.createdAt).format('DD/MM/YYYY HH:mm:ss'),
      'Updated At': moment(bank.updatedAt).format('DD/MM/YYYY HH:mm:ss')
    }));
    setExportData(data);
    return data;
  }, [fetchAllBanksForExport]);

  /** EXPORT TO CSV */
  const handleExportCSV = async () => {
    const data = await prepareExportData();
    if (!data.length) return;
    
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  };

  /** EXPORT TO EXCEL */
  const handleExportExcel = async () => {
    const data = await prepareExportData();
    if (!data.length) {
      toast.warning("No data to export");
      return;
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "FING Banks");
      
      // Auto-size columns
      const colWidths = data.reduce((widths, row) => {
        Object.keys(row).forEach((key, idx) => {
          const cellLength = String(row[key]).length;
          widths[idx] = Math.max(widths[idx] || 10, cellLength);
        });
        return widths;
      }, []);
      
      worksheet['!cols'] = colWidths.map(w => ({ wch: w + 2 }));
      
      XLSX.writeFile(workbook, `fing_banks_export_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file");
    }
  };

  /** TOGGLE BANK STATUS */
  const toggleBankStatus = async (bank, newStatus) => {
    try {
      const payload = {
        ...bank,
        activeFlag: newStatus ? 1 : 0
      };
      
      const res = await postRequest(`${API_ENDPOINTS.UPDATE}/${bank._id}`, payload, history, "PUT");
      
      if (res?.success) {
        toast.success(`Bank ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchBanks();
      } else {
        toast.error("Failed to update bank status");
      }
    } catch (error) {
      toast.error("Error updating bank status");
    }
  };

  /** RESET ALL FILTERS */
  const handleResetFilters = () => {
    setSearchTerm("");
    setSearchBy("bankName");
    setPagination({
      current: 1,
      pageSize: 50,
      total: 0,
      pages: 1
    });
  };

  /** TABLE COLUMNS (including new fields) */
  const columns = [
    {
      name: "Bank ID",
      selector: row => row.bankId,
      sortable: true,
      width: '100px',
      cell: row => row.bankId ? (
        <Tag color="blue" style={{ fontWeight: 600, fontSize: '12px' }}>
          #{row.bankId}
        </Tag>
      ) : (
        <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      )
    },
    {
      name: "Bank Name",
      selector: row => row.bankName,
      sortable: true,
      cell: row => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.bankName}</Text>
        </Space>
      )
    },
    {
      name: "Face Auth",
      selector: row => row.face_auth,
      sortable: true,
      width: '110px',
      cell: row => (
        <Tag color={row.face_auth === 'Y' ? "green" : "default"} style={{ fontSize: '11px' }}>
          {row.face_auth === 'Y' ? 'Enabled' : 'Disabled'}
        </Tag>
      )
    },
    {
      name: "Aadhar Pay",
      selector: row => row.aadharPay,
      sortable: true,
      width: '110px',
      cell: row => (
        <Tag color={row.aadharPay === 'Y' ? "green" : "red"} style={{ fontSize: '11px' }}>
          {row.aadharPay === 'Y' ? 'Enabled' : 'Disabled'}
        </Tag>
      )
    },
    {
      name: "Cash Deposit",
      selector: row => row.cashDeposit,
      sortable: true,
      width: '120px',
      cell: row => (
        <Tag color={row.cashDeposit === 'Y' ? "green" : "red"} style={{ fontSize: '11px' }}>
          {row.cashDeposit === 'Y' ? 'Enabled' : 'Disabled'}
        </Tag>
      )
    },
    {
      name: "IIN Number",
      selector: row => row.iinno,
      sortable: true,
      width: '120px',
      cell: row => row.iinno ? (
        <Text style={{ fontSize: '11px', fontWeight: 500 }}>{row.iinno}</Text>
      ) : (
        <Text type="secondary" style={{ fontSize: '11px' }}>—</Text>
      )
    },
    {
      name: "Remarks",
      selector: row => row.remarks,
      cell: row => row.remarks ? (
        <Tooltip title={row.remarks}>
          <Text style={{ fontSize: '11px' }} ellipsis>
            {row.remarks.substring(0, 30)}{row.remarks.length > 30 ? '...' : ''}
          </Text>
        </Tooltip>
      ) : (
        <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      )
    },
    {
      name: "Status",
      width: '100px',
      cell: row => (
        <Space>
          <Tag 
            color={row.activeFlag === 1 ? "success" : "error"} 
            style={{ fontSize: '11px' }}
          >
            {row.activeFlag === 1 ? "Active" : "Inactive"}
          </Tag>
        </Space>
      )
    },
    {
      name: "Actions",
      width: '100px',
      cell: row => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEditModal(row)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this FING bank?"
            description="Are you sure you want to delete this bank?"
            onConfirm={() => handleRemove(row._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                loading={removingId === row._id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // CSV headers for export (updated with new fields)
  const csvHeaders = [
    { label: 'Bank ID', key: 'Bank ID' },
    { label: 'Bank Name', key: 'Bank Name' },
    { label: 'IIN Number', key: 'IIN Number' },
    { label: 'Face Auth', key: 'Face Auth' },
    { label: 'Aadhar Pay', key: 'Aadhar Pay' },
    { label: 'Cash Deposit', key: 'Cash Deposit' },
    { label: 'Status', key: 'Status' },
    { label: 'Remarks', key: 'Remarks' },
    { label: 'Details', key: 'Details' },
    { label: 'Timestamp', key: 'Timestamp' },
    { label: 'Created At', key: 'Created At' },
    { label: 'Updated At', key: 'Updated At' }
  ];

  // Initial fetch
  useEffect(() => {
    fetchBanks();
  }, []);

  // Fetch when pagination or filters change
  useEffect(() => {
    fetchBanks();
  }, [pagination.current, pagination.pageSize, searchTerm]);

  return (
    <div>
      {/* ACTION BUTTONS CARD */}
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          marginBottom: 24,
          background: "rgba(255, 255, 255, 0.9)",
          boxShadow: "0 2px 12px rgba(0,0,0,.04)"
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <Space direction="vertical" size={0}>
              <Title level={4} style={{ margin: 0, color: "#1a1a1a" }}>
                <SecurityScanOutlined /> FING Bank Management
              </Title>
              <Text type="secondary">
                  {pagination.total} banks found • Page {pagination.current} of {pagination.pages}
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }} wrap>
              <Button
                icon={<ImportOutlined />}
                size="middle"
                onClick={() => setImportModalVisible(true)}
              >
                Bulk Import
              </Button>
              
              <Button
                icon={<FileExcelOutlined />}
                size="middle"
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
              
              <Button
                icon={<DownloadOutlined />}
                size="middle"
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
              
              <Button
                icon={<PlusOutlined />}
                type="primary"
                size="middle"
                onClick={openAddModal}
                style={{
                  background: "linear-gradient(135deg,#10b981,#059669)",
                  border: "none",
                  fontWeight: 600
                }}
              >
                Add FING Bank
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* SEARCH CARD */}
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          marginBottom: 24,
          background: "rgba(255, 255, 255, 0.9)",
          boxShadow: "0 2px 12px rgba(0,0,0,.04)"
        }}
      >
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} md={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Search By</Text>
              <Select
                value={searchBy}
                onChange={setSearchBy}
                style={{ width: "100%" }}
                size="middle"
              >
                <Option value="bankName">Bank Name</Option>
                <Option value="iinno">IIN Number</Option>
                <Option value="bankId">Bank ID</Option>
                <Option value="remarks">Remarks</Option>
              </Select>
            </Space>
          </Col>
          
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Search Term</Text>
              <Input
                placeholder={`Search by ${searchBy}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="middle"
                onPressEnter={handleSearch}
                prefix={<SearchOutlined />}
                style={{ borderRadius: 8 }}
              />
            </Space>
          </Col>
          
          <Col xs={24} md={6}>
            <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 28 }}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                size="middle"
                style={{ borderRadius: 8, height: 32 }}
              >
                Search
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilters}
                size="middle"
                style={{ borderRadius: 8, height: 32 }}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* MAIN TABLE CARD */}
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          background: "rgba(255, 255, 255, 0.9)",
          boxShadow: "0 2px 12px rgba(0,0,0,.04)"
        }}
        bodyStyle={{ padding: 0 }}
      >
        <DataTable
          columns={columns}
          data={banks}
          progressPending={loading}
          highlightOnHover
          pagination
          paginationServer
          paginationTotalRows={pagination.total}
          paginationDefaultPage={pagination.current}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangePage={handleTableChange}
          onChangeRowsPerPage={handlePerRowsChange}
          noDataComponent={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">
                  No FING banks found. Add your first bank using the "Add FING Bank" button.
                </Text>
              }
              style={{ padding: "40px 0" }}
            />
          }
          customStyles={{
            headRow: {
              style: {
                backgroundColor: "#f8fafc",
                borderBottom: "2px solid #e2e8f0",
                fontSize: '12px',
                fontWeight: 600
              },
            },
            rows: {
              style: {
                fontSize: "13px",
                "&:hover": {
                  backgroundColor: "#f1f5f9 !important",
                },
              },
            },
            cells: {
              style: {
                padding: '12px 8px',
              }
            }
          }}
        />
      </Card>

      {/* Hidden CSV Download Link */}
      <CSVLink
        ref={csvLinkRef}
        data={exportData}
        headers={csvHeaders}
        filename={`fing_banks_export_${moment().format('YYYYMMDD_HHmmss')}.csv`}
        style={{ display: 'none' }}
      />

      {/* ADD/EDIT FING BANK MODAL */}
      <Modal
        title={
          <Space>
            <SecurityScanOutlined />
            <Text strong>{modalTitle}</Text>
          </Space>
        }
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bankId"
                label={
                  <Space>
                    <NumberOutlined />
                    <span>Bank ID {!editingBank && <Text type="secondary">(Optional)</Text>}</span>
                  </Space>
                }
                rules={[
                  { 
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (isNaN(value)) return Promise.reject('Bank ID must be a number');
                      if (value <= 0) return Promise.reject('Bank ID must be a positive number');
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  placeholder="Auto-generated if left empty"
                  size="middle"
                  prefix={<NumberOutlined />}
                  disabled={editingBank}
                  type="number"
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bankName"
                label={
                  <Space>
                    <BankOutlined />
                    <span>Bank Name *</span>
                  </Space>
                }
                rules={[
                  { required: true, message: "Please enter bank name" },
                  { max: 100, message: "Bank name cannot exceed 100 characters" }
                ]}
              >
                <Input
                  placeholder="Enter bank name"
                  size="middle"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="iinno"
                label="IIN Number"
                rules={[{ max: 50, message: "IIN number cannot exceed 50 characters" }]}
              >
                <Input
                  placeholder="Enter IIN number"
                  size="middle"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="face_auth"
                label="Face Authentication"
              >
                <Select size="middle">
                  <Option value="Y">
                    <Tag color="green">Enabled</Tag>
                  </Option>
                  <Option value="N">
                    <Tag color="default">Disabled</Tag>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="aadharPay"
                label="Aadhar Pay"
              >
                <Select size="middle">
                  <Option value="Y">
                    <Tag color="green">Enabled</Tag>
                  </Option>
                  <Option value="N">
                    <Tag color="red">Disabled</Tag>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cashDeposit"
                label="Cash Deposit"
              >
                <Select size="middle">
                  <Option value="Y">
                    <Tag color="green">Enabled</Tag>
                  </Option>
                  <Option value="N">
                    <Tag color="red">Disabled</Tag>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="activeFlag"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>

          <Form.Item
            name="remarks"
            label="Remarks"
            rules={[{ max: 500, message: "Remarks cannot exceed 500 characters" }]}
          >
            <Input.TextArea
              placeholder="Enter remarks (optional)"
              rows={2}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="details"
            label="Details"
            rules={[{ max: 1000, message: "Details cannot exceed 1000 characters" }]}
          >
            <Input.TextArea
              placeholder="Enter additional details (optional)"
              rows={3}
              showCount
              maxLength={1000}
            />
          </Form.Item>

          <Form.Item
            name="timestamp"
            label="Timestamp"
          >
            <Input
              placeholder="YYYY-MM-DD HH:mm:ss"
              size="middle"
              prefix={<CalendarOutlined />}
            />
          </Form.Item>

          {editingBank && (
            <Alert
              message="Note"
              description="Bank ID cannot be changed for existing records."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={closeModal} size="middle">
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={actionLoading}
                size="middle"
                style={{
                  background: "linear-gradient(135deg,#10b981,#059669)",
                  border: "none",
                  fontWeight: 600
                }}
              >
                {editingBank ? "Update Bank" : "Add Bank"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* BULK IMPORT MODAL (with new fields) */}
      <Modal
        title="Bulk Import FING Banks"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert
            message="Import Instructions"
            description="Upload an Excel (.xlsx) or CSV file with columns: bankId, bankName, iinno, face_auth, aadharPay, cashDeposit, activeFlag, remarks, details, timestamp. Use 'Y' for enabled, 'N' for disabled. Defaults: face_auth='N', aadharPay='Y', cashDeposit='Y', activeFlag=1."
            type="info"
            showIcon
          />
          <Upload
            beforeUpload={(file) => {
              setImportFile(file);
              const reader = new FileReader();
              reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet);
                setImportPreview(json);
              };
              reader.readAsArrayBuffer(file);
              return false;
            }}
            accept=".xlsx,.csv"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
          {importPreview.length > 0 && (
            <>
              <Divider orientation="left">Preview (first 5 rows)</Divider>
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <DataTable
                  columns={[
                    { name: "Bank Name", selector: row => row.bankName },
                    { name: "IIN", selector: row => row.iinno },
                    { name: "Face Auth", selector: row => row.face_auth },
                    { name: "Aadhar Pay", selector: row => row.aadharPay },
                    { name: "Cash Deposit", selector: row => row.cashDeposit }
                  ]}
                  data={importPreview.slice(0, 5)}
                />
              </div>
              <Button
                type="primary"
                loading={importing}
                onClick={async () => {
                  setImporting(true);
                  try {
                    const res = await postRequest(API_ENDPOINTS.IMPORT, { data: importPreview }, history, "POST");
                    if (res?.success) {
                      toast.success(`Imported ${res.insertedCount || importPreview.length} banks`);
                      setImportModalVisible(false);
                      setImportFile(null);
                      setImportPreview([]);
                      fetchBanks();
                    } else {
                      toast.error(res?.message || "Import failed");
                    }
                  } catch (err) {
                    toast.error("Import error");
                  } finally {
                    setImporting(false);
                  }
                }}
                style={{ marginTop: 16 }}
              >
                Confirm Import
              </Button>
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default FINGBankManagement;